import { NavLink, useLocation, Link } from "react-router-dom";
import {
    Box,
    Divider,
    Dropdown,
    Menu,
    MenuButton,
    MenuItem,
    Typography,
    useColorScheme,
} from "@mui/joy";

import defaultLogoUrl from "../../assets/images/logo.png";

import {
    FileStack,
    Layers,
    LayoutDashboard,
    Store,
    ShieldHalf,
    Logs,
    Fuel,
    BarChartBig,
    Bell,
    ArrowLeft,
    Plus,
    CalendarRange,
    Settings2,
    Tag,
    LifeBuoy,
    FlaskConical,
    Settings,
    Sun,
    Moon,
    Users,
    Building2,
    ChevronsUpDown,
    Check,
    Plug,
    type LucideIcon,
} from "lucide-react";

import { Gate } from "../../shared";
import { PERMISSIONS, ROLES } from "../../constants/permissions";

import { IconButton } from "../../shared";
import "./index.scss";
import { useProject } from "../../hooks/query";
import { IProjectList } from "../../interfaces";
import { useAuth, usePastelColors, useAuthorization } from "../../hooks";
import { AuthenticatedImage } from "../AuthenticatedImage";

interface NavItemConfig {
    name: string;
    href: string;
    icon: LucideIcon;
}

function BrandLogo({ collapsed }: { collapsed?: boolean }) {
    const { selectedOrganization } = useAuth();
    const logoValue = selectedOrganization?.LogoUrl || "";
    const height = 61;
    const width = collapsed ? 61 : "auto";
    const isAssetLogo = logoValue.startsWith("ast-");
    const isExternalLogo = !!logoValue && !isAssetLogo;

    if (isAssetLogo) {
        return (
            <AuthenticatedImage
                assetId={logoValue}
                alt={selectedOrganization?.Name || "Organization logo"}
                width={61}
                height={height}
                borderRadius="4px"
                objectFit="contain"
                sx={{ flexShrink: 0 }}
            />
        );
    }

    return (
        <img
            src={isExternalLogo ? logoValue : defaultLogoUrl}
            alt={selectedOrganization?.Name || "Roof in Motion"}
            style={{
                height,
                width,
                maxWidth: collapsed ? 61 : 160,
                objectFit: "contain",
                transition: "height 0.2s ease, width 0.2s ease",
            }}
        />
    );
}

export default function NavigationPanel({
    collapsed,
}: {
    collapsed?: boolean;
}) {
    const { pathname } = useLocation();
    const isAdminRoute = pathname.startsWith("/admin");
    const { mode, setMode } = useColorScheme();

    return (
        <Box className={`NavigationWrapper ${collapsed ? "collapsed" : ""}`}>
            <Box className="nav-brand">
                <BrandLogo collapsed={collapsed} />
            </Box>

            <Box className="nav-scroll">
                {isAdminRoute ? (
                    <NavigationPanelAdmin collapsed={collapsed} />
                ) : (
                    <NavigationPanelMenu collapsed={collapsed} />
                )}
            </Box>
            <Divider sx={{ mb: 0.5 }} />
            <OrganizationSwitcher collapsed={collapsed} />
            <Divider sx={{ mt: 0.5 }} />

            <Box
                className="nav-footer"
                sx={{
                    pb: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.4,
                }}
            >
                <Gate anyOf={[PERMISSIONS.ADMIN_SETTINGS]}>
                    <NavigationItem
                        name="Settings"
                        href="/admin/manufacture/"
                        icon={Settings}
                        collapsed={collapsed}
                    />
                </Gate>
                <Box
                    component="button"
                    onClick={() => setMode(mode === "light" ? "dark" : "light")}
                    className={`navigation-item ${collapsed ? "collapsed-item" : ""}`}
                    sx={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                        fontFamily: "inherit",
                        color: "inherit",
                    }}
                    title={mode === "light" ? "Dark mode" : "Light mode"}
                >
                    {mode === "light" ? <Moon size={18} /> : <Sun size={18} />}
                    {!collapsed && (
                        <Typography
                            level="body-sm"
                            sx={{ fontWeight: 500, color: "inherit" }}
                        >
                            {mode === "light" ? "Dark mode" : "Light mode"}
                        </Typography>
                    )}
                    {collapsed && (
                        <Typography
                            level="body-xs"
                            sx={{ fontWeight: 500, color: "inherit" }}
                        >
                            {mode === "light" ? "Dark" : "Light"}
                        </Typography>
                    )}
                </Box>
                <Divider />
                <NavigationItem
                    name={collapsed ? "Help" : "Help & Documentation"}
                    href="/help"
                    icon={LifeBuoy}
                    collapsed={collapsed}
                />
            </Box>
        </Box>
    );
}

function OrganizationSwitcher({ collapsed }: { collapsed?: boolean }) {
    const { organizations, selectedOrganization, setSelectedOrganization } =
        useAuth();

    if (!organizations.length || !selectedOrganization) {
        return null;
    }

    const canSwitch = organizations.length > 1;
    const displayName =
        selectedOrganization.ShortName ||
        selectedOrganization.Name ||
        "Organization";

    const label = (
        <>
            <Building2 size={collapsed ? 20 : 18} />
            {collapsed ? (
                <Typography
                    level="body-xs"
                    sx={{
                        fontWeight: 500,
                        color: "inherit",
                        fontSize: "10px",
                        lineHeight: 1,
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {displayName}
                </Typography>
            ) : (
                <>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            level="title-sm"
                            sx={{
                                color: "inherit",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {selectedOrganization.Name}
                        </Typography>
                    </Box>
                    {canSwitch ? <ChevronsUpDown size={14} /> : null}
                </>
            )}
        </>
    );

    if (!canSwitch) {
        return (
            <Box
                className={`navigation-item ${collapsed ? "collapsed-item" : ""}`}
                title={selectedOrganization.Name}
                sx={{ cursor: "default" }}
            >
                {label}
            </Box>
        );
    }

    return (
        <Dropdown>
            <MenuButton
                variant="plain"
                color="neutral"
                className={`navigation-item ${collapsed ? "collapsed-item" : ""}`}
                title={selectedOrganization.Name}
                sx={{
                    background: "transparent",
                    border: "none",
                    borderRadius: 0,
                    justifyContent: collapsed ? "center" : "flex-start",
                    fontWeight: "inherit",
                    color: "inherit",
                    minHeight: "unset",
                    "&:hover": {
                        background: "var(--standard-palette-neutral-softBg)",
                        color: "var(--standard-palette-text-primary)",
                    },
                }}
            >
                {label}
            </MenuButton>
            <Menu
                size="sm"
                placement={collapsed ? "right-end" : "top-start"}
                sx={{ minWidth: 220, zIndex: 1400 }}
            >
                {organizations.map((org) => {
                    const isSelected = org.Id === selectedOrganization.Id;
                    return (
                        <MenuItem
                            key={org.Id}
                            selected={isSelected}
                            onClick={() => setSelectedOrganization(org)}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    level="body-sm"
                                    sx={{ fontWeight: isSelected ? 600 : 500 }}
                                >
                                    {org.Name}
                                </Typography>
                                {org.ShortName ? (
                                    <Typography
                                        level="body-xs"
                                        sx={{ color: "text.tertiary" }}
                                    >
                                        {org.ShortName}
                                    </Typography>
                                ) : null}
                            </Box>
                            {isSelected ? <Check size={14} /> : null}
                        </MenuItem>
                    );
                })}
            </Menu>
        </Dropdown>
    );
}

function NavigationPanelAdmin({ collapsed }: { collapsed?: boolean }) {
    const { hasRole } = useAuthorization();
    const isSuperAdmin = hasRole(ROLES.SUPER_ADMIN);

    const visibleMenus = adminMenus
        .map((section) => ({
            ...section,
            menu: section.menu.filter((item) => {
                if (item.href === "/admin/organizations") {
                    return isSuperAdmin;
                }
                return true;
            }),
        }))
        .filter((section) => section.menu.length > 0);

    return (
        <Box className="nav-section">
            <Link to="/" className="nav-back-row">
                <ArrowLeft size={16} />
                {!collapsed && (
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                        Back to app
                    </Typography>
                )}
            </Link>

            {visibleMenus.map((section) => (
                <Box key={section.category}>
                    {!collapsed && (
                        <span className="nav-section-label">
                            {section.category}
                        </span>
                    )}
                    {section.menu.map((menuItem) => (
                        <NavigationItem
                            {...menuItem}
                            key={menuItem.name}
                            collapsed={collapsed}
                        />
                    ))}
                </Box>
            ))}
        </Box>
    );
}

function NavigationPanelMenu({ collapsed }: { collapsed?: boolean }) {
    const { useList } = useProject();
    const { data } = useList();
    const projects: IProjectList[] = Array.isArray(data) ? data : [];

    return (
        <Box>
            <Box className="nav-section">
                {menus.map((item) => (
                    <NavigationItem
                        {...item}
                        key={item.name}
                        collapsed={collapsed}
                    />
                ))}
            </Box>

            {!collapsed && (
                <Box>
                    <Box className="nav-projects-header">
                        <Typography
                            level="body-sm"
                            sx={{ fontWeight: 600, color: "text.primary" }}
                        >
                            Recent projects
                        </Typography>
                        <IconButton
                            component={Link}
                            to="/project/new"
                            color="neutral"
                            variant="plain"
                            size="sm"
                            sx={{ "--IconButton-size": "28px" }}
                        >
                            <Plus size={14} />
                        </IconButton>
                    </Box>
                    <Box pb={1}>
                        {projects.slice(0, 10).map((project) => (
                            <ProjectItem key={project.Id} {...project} />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

function ProjectItem(props: IProjectList) {
    const { Color, Name, Id } = props;
    const pastel = usePastelColors(Id);
    const dotColor = Color || pastel;

    return (
        <Link to={`/project/list/${Id}`} className="nav-project-row">
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: dotColor,
                    flexShrink: 0,
                }}
            />
            <Typography
                level="body-sm"
                sx={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {Name}
            </Typography>
        </Link>
    );
}

function isNavActive(pathname: string, href: string) {
    if (href === "/") {
        return pathname === href;
    }
    // Exact segment match so /admin/org does not match /admin/organizations
    return pathname === href || pathname.startsWith(`${href}/`);
}

function NavigationItem({
    name,
    href,
    icon: Icon,
    collapsed,
}: NavItemConfig & { collapsed?: boolean }) {
    const { pathname } = useLocation();
    const isActive = isNavActive(pathname, href);

    return (
        <NavLink
            to={href}
            className={`navigation-item ${isActive ? "active" : ""} ${collapsed ? "collapsed-item" : ""}`}
            title={name}
        >
            <Icon
                size={collapsed ? 20 : 18}
                strokeWidth={isActive ? 2.25 : 2}
            />
            {collapsed ? (
                <Typography
                    level="body-xs"
                    sx={{
                        fontWeight: isActive ? 600 : 500,
                        color: "inherit",
                        fontSize: "10px",
                        lineHeight: 1,
                    }}
                >
                    {name}
                </Typography>
            ) : (
                <Typography
                    level="body-sm"
                    sx={{ fontWeight: isActive ? 600 : 500, color: "inherit" }}
                >
                    {name}
                </Typography>
            )}
        </NavLink>
    );
}

const menus: NavItemConfig[] = [
    { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
    { name: "Projects", href: "/project", icon: Layers },
    { name: "Reports", href: "/reports/list", icon: FileStack },
    { name: "Suppliers", href: "/suppliers", icon: Store },
    { name: "Calendar", href: "/calendar", icon: CalendarRange },
];

const adminMenus = [
    {
        category: "Tenant",
        menu: [
            {
                name: "Organizations",
                href: "/admin/organizations",
                icon: Store,
            },
            { name: "Admins", href: "/admin/tenant/admins", icon: Users },
        ],
    },
    {
        category: "Platform",
        menu: [
            { name: "Organization", href: "/admin/org", icon: Building2 },
            { name: "Manufacturers", href: "/admin/manufacture", icon: Layers },
            {
                name: "Product Categories",
                href: "/admin/product-category",
                icon: Tag,
            },
            {
                name: "Formulas & Mapping",
                href: "/admin/formulas",
                icon: FlaskConical,
            },
            { name: "Integrations", href: "/admin/integrations", icon: Plug },
            { name: "General", href: "/admin/general", icon: Settings2 },
        ],
    },
    {
        category: "User Management",
        menu: [
            { name: "Users", href: "/admin/users", icon: FileStack },
            { name: "Roles", href: "/admin/roles", icon: ShieldHalf },
        ],
    },
    {
        category: "Advanced",
        menu: [
            { name: "Audit Logs", href: "/admin/audit-logs", icon: Logs },
            { name: "Billing", href: "/admin/billing", icon: Fuel },
            { name: "Notifications", href: "/admin/billing", icon: Bell },
        ],
    },
    {
        category: "Reports",
        menu: [
            { name: "Analytics", href: "/admin/reports", icon: BarChartBig },
        ],
    },
];
